import { useEffect, useState } from 'react';

/**
 * @param {number} duration - Video süresi (saniye)
 * @param {number} minSpacing - Popup'lar arası minimum saniye aralığı
 * @param {number} margin - Videonun başı ve sonundan uzak durulacak süre (varsayılan: 60s)
 * @param {number} jitter - tahmin edilemezlik, rastlantısal sapmalar (jitter) ekle
 *
 *  * Hedefler:
 *  * 1. Video süresine göre popup sayısı otomatik belirlenir
 *  * 2. Kısa videolarda hiç popup olmayabilir
 *  * 3. Popup'lar başlangıç ve bitişe çok yakın olmaz (margin)
 *  * 4. Popup'lar arası minimum mesafe olur (minSpacing)
 *  * 5. Rastlantısal sapma (jitter) ile tahmin edilemez zamanlar yaratılır
 *  * 6. Sonsuz döngüye karşı koruma (maxTries)
 *  */
export function generatePopupTimePoints(duration, {
    margin = 60, // Başlangıç ve bitişten uzak durma (saniye)
    minSpacing = 45, // Popup'lar arası minimum fark (saniye)
    jitterRange = 60 // Jitter toplam aralığı (örneğin ±30 saniye için 60
} = {}) {
    if (!duration || duration <= 60) return [];

    const getPopupCount = () => {
        if (duration < 300) {
            return Math.random() < 0.5 ? 0 : 1;        // <5 dk: %50 ihtimalle 1 veya 0 popup
        } else if (duration < 2700) {
            return 2 + Math.floor(Math.random() * 2);  // 5-45 dk: 2 veya 3 popup
        } else {
            return 4 + Math.floor(Math.random() * 2);  // 45+ dk: 4 veya 5 popup
        }
        // return 3; // sabit 3 popup dön, test amaçlı
    };

    const generatePoints = () => {
        const count = getPopupCount();
        const points = new Set();
        const maxTries = 1000;
        let attempts = 0;

        while (points.size < count && attempts++ < maxTries) {
            const base = Math.floor(Math.random() * (duration - 2 * margin)) + margin;
            const jitter = Math.floor(Math.random() * jitterRange) - jitterRange / 2;
            const final = Math.max(margin, Math.min(duration - margin, base + jitter));
            const tooClose = Array.from(points).some(p => Math.abs(p - final) < minSpacing);
            if (!tooClose) points.add(Math.floor(final));
        }

        return Array.from(points).sort((a, b) => a - b);
    };

    return generatePoints();
}

export function useRandomTimePoints(duration) {
    const [targets, setTargets] = useState([]);

    useEffect(() => {
        setTargets(generatePopupTimePoints(duration));
    }, [duration]);

    return targets;
}